import type { ProposedAction } from '../model/types';

export function topologicalSort(actions: readonly ProposedAction[]): ProposedAction[] {
  if (actions.length <= 1) {
    return [...actions];
  }

  const actionByExternalId = new Map<string, ProposedAction>();
  const inputOrder = new Map<string, number>();
  for (const [i, action] of actions.entries()) {
    actionByExternalId.set(action.external_id, action);
    inputOrder.set(action.external_id, i);
  }

  const indegree = new Map<string, number>();
  const outgoing = new Map<string, string[]>();
  for (const action of actions) {
    indegree.set(action.external_id, 0);
    outgoing.set(action.external_id, []);
  }

  for (const action of actions) {
    const seenDependencies = new Set<string>();
    for (const dependencyExternalId of action.depends_on_action_external_ids) {
      if (seenDependencies.has(dependencyExternalId)) {
        continue;
      }
      seenDependencies.add(dependencyExternalId);

      if (!actionByExternalId.has(dependencyExternalId)) {
        continue;
      }

      if (dependencyExternalId === action.external_id) {
        continue;
      }

      const dependents = outgoing.get(dependencyExternalId);
      if (dependents === undefined) {
        continue;
      }
      dependents.push(action.external_id);
      indegree.set(action.external_id, (indegree.get(action.external_id) ?? 0) + 1);
    }
  }

  const queue: string[] = [];
  for (const action of actions) {
    if ((indegree.get(action.external_id) ?? 0) === 0) {
      queue.push(action.external_id);
    }
  }
  queue.sort((a, b) => (inputOrder.get(a) ?? 0) - (inputOrder.get(b) ?? 0));

  const sorted: ProposedAction[] = [];
  let cursor = 0;
  while (cursor < queue.length) {
    const externalId = queue[cursor];
    cursor += 1;
    if (externalId === undefined) {
      continue;
    }

    const action = actionByExternalId.get(externalId);
    if (action === undefined) {
      continue;
    }
    sorted.push(action);

    const dependents = outgoing.get(externalId) ?? [];
    for (const dependentExternalId of dependents) {
      const next = (indegree.get(dependentExternalId) ?? 0) - 1;
      indegree.set(dependentExternalId, next);
      if (next === 0) {
        queue.push(dependentExternalId);
      }
    }
  }

  if (sorted.length !== actions.length) {
    // Cycle detected; preserve input order as a defensive fallback.
    return [...actions];
  }

  return sorted;
}
